import { FilterableSelect, FormField } from '@expressable/syntax-ds';
import {
  OTHER_INSURANCE_OPTION,
  SIGNUP_PRIVATE_PAY_OPTION,
  useStateInsuranceTypeOptions,
} from 'domains/signupDirect/misc/constants';
import { DirectSignupFiltersFormType } from 'domains/signupDirect/schemas/filtersSchema';
import { isEmpty } from 'lodash';
import { FC } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StateAbbrev } from 'utils/states';

type Props = {
  state?: StateAbbrev;
};

export const InsuranceSelect: FC<Props> = ({ state }) => {
  const { t } = useTranslation();

  const { field } = useController<Partial<DirectSignupFiltersFormType>, 'paymentMethod'>({
    name: 'paymentMethod',
  });

  const { options: insuranceOptions, insuranceData } = useStateInsuranceTypeOptions({
    stateFilter: state,
  });

  const onChange = (value: string) => {
    if (!insuranceData) throw 'Expected insuranceData to be set';
    const isUndefined = isEmpty(value);
    const isPrivatePay = value === SIGNUP_PRIVATE_PAY_OPTION;
    const isOtherInsurance = value === OTHER_INSURANCE_OPTION;
    const currentInsurance = insuranceData.find(i => i.entryId === value);

    if (isPrivatePay) {
      field.onChange({
        type: 'privatePay',
        id: SIGNUP_PRIVATE_PAY_OPTION,
      } as DirectSignupFiltersFormType['paymentMethod']);
    } else if (isOtherInsurance) {
      field.onChange({
        type: 'otherInsurance',
        id: OTHER_INSURANCE_OPTION,
      } as DirectSignupFiltersFormType['paymentMethod']);
    } else if (currentInsurance) {
      field.onChange({
        type: 'insurance',
        id: currentInsurance.entryId,
        insuranceData: currentInsurance,
      } as DirectSignupFiltersFormType['paymentMethod']);
    } else if (isUndefined) {
      field.onChange(undefined);
    } else {
      throw 'Expected insurance to be set when NOT private pay';
    }
  };

  return (
    <FormField label={t('directSignup.filters.paymentMethod')} name="paymentMethod">
      <FilterableSelect
        data-testid="payment-method-select"
        options={insuranceOptions ?? []}
        disabled={isEmpty(state)}
        onChange={onChange}
        standalone
        value={field.value?.id ?? ''}
      />
    </FormField>
  );
};
